/**
 * PlayerStatsTable - Compact Tufte-styled player stats table with streak badges.
 *
 * Sport-aware column definitions. Minimal table: no vertical lines,
 * thin bottom borders, font-mono numbers.
 *
 * @param {Array} players - Player analytics objects from game players endpoint
 * @param {Object} team - {name, abbreviation, logo}
 * @param {boolean} isHome - Whether this is the home team
 * @param {Function} onPlayerClick - Click handler for player rows
 * @param {string} sport - Sport identifier (nba, nhl, nfl)
 */
import { useMemo } from 'react';
import Card from '../../common/Card';

const badgeColors = {
  blazing: 'bg-orange-400',
  hot: 'bg-red-400',
  warm: 'bg-amber-400',
};

const getTrendColor = (color) => {
  if (color === 'green') return 'text-green-400';
  if (color === 'red') return 'text-red-400';
  return 'text-text-secondary';
};

const getColumns = (sport) => {
  switch (sport?.toLowerCase()) {
    case 'nba':
      return [
        { key: 'points', label: 'PTS' },
        { key: 'rebounds', label: 'REB' },
        { key: 'assists', label: 'AST' },
        { key: 'threes', label: '3PM' },
        { key: 'steals', label: 'STL' },
        { key: 'blocks', label: 'BLK' },
      ];
    case 'nhl':
      return [
        { key: 'goals', label: 'G' },
        { key: 'assists', label: 'A' },
        { key: 'points', label: 'PTS' },
        { key: 'shots', label: 'SOG' },
      ];
    case 'nfl':
    case 'ncaa-f':
    case 'ncaa_f':
      return [
        { key: 'passYds', label: 'PASS' },
        { key: 'rushYds', label: 'RUSH' },
        { key: 'recYds', label: 'REC' },
        { key: 'receptions', label: 'REC#' },
      ];
    case 'mlb':
      return [
        { key: 'hits', label: 'H' },
        { key: 'total_bases', label: 'TB' },
        { key: 'home_runs', label: 'HR' },
        { key: 'rbis', label: 'RBI' },
        { key: 'runs', label: 'R' },
      ];
    case 'ncaa-b':
    case 'ncaa_b':
    default:
      // Basketball shape. Reached only when the API sends no column metadata --
      // it used to be the silent default for MLB as well, which is how baseball
      // ended up with PTS / REB / AST headers over empty cells.
      return [
        { key: 'points', label: 'PTS' },
        { key: 'rebounds', label: 'REB' },
        { key: 'assists', label: 'AST' },
      ];
  }
};

const PlayerStatsTable = ({ players = [], team = {}, isHome = false, onPlayerClick, sport = 'nba' }) => {
  // Prefer the columns the API sends with each player. The endpoint already
  // returns the right ones per sport -- MLB comes back as H / TB / HR / RBI --
  // and getColumns() below has no 'mlb' case, so it fell through to a default of
  // PTS / REB / AST. That rendered basketball headers over baseball players and,
  // because no MLB row has a `points` or `rebounds` key, every cell came out
  // blank: the "no data" symptom and the wrong headers were one bug.
  //
  // 'split' is the row label (Season / Last 3), not a stat, so it is dropped.
  // getColumns stays as the fallback for any payload without column metadata.
  const columns = useMemo(() => {
    const fromApi = players.find(p => Array.isArray(p?.columns) && p.columns.length > 0)?.columns;
    if (fromApi) {
      return fromApi.filter(c => c.key !== 'split');
    }
    return getColumns(sport);
  }, [players, sport]);

  // Parse player data from the analytics format
  const parsedPlayers = useMemo(() => {
    return players.map(playerData => {
      const seasonRow = playerData.rows?.find(r => r.label === 'Season');
      const l3Row = playerData.rows?.find(r => r.label === 'Last 3');
      // NHL uses Avg row from OLAP
      const avgRow = playerData.rows?.find(r => r.data?.split?.value === 'Avg' || r.data?.split?.value === 'Season');

      const seasonStats = seasonRow?.data || avgRow?.data || {};
      const l3Stats = l3Row?.data || {};

      return {
        id: playerData.player?.id,
        name: playerData.player?.name,
        position: playerData.player?.position,
        teamId: playerData.player?.team_id,
        badge: playerData.player?.badge,
        streaks: playerData.streaks || [],
        seasonStats,
        l3Stats,
      };
    })
    // Sort by the leading stat column descending, so the most productive players
    // are on top. Keyed off the first real column rather than a hardcoded
    // 'points', which does not exist for baseball and left MLB in arbitrary order.
    .sort((a, b) => {
      const sortKey = columns[0]?.key;
      if (!sortKey) return 0;
      const aVal = a.seasonStats?.[sortKey]?.value ?? 0;
      const bVal = b.seasonStats?.[sortKey]?.value ?? 0;
      return bVal - aVal;
    });
  }, [players, columns]);

  if (parsedPlayers.length === 0) return null;

  return (
    <Card className="p-0 overflow-hidden">
      {/* Team header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-border/30">
        {team.logo && (
          <img src={team.logo} alt={team.abbreviation} className="w-6 h-6 object-contain" />
        )}
        <span className="text-sm font-semibold text-text-primary">{team.name || (isHome ? 'Home' : 'Away')}</span>
        <span className="text-[10px] text-text-secondary ml-auto">{parsedPlayers.length} players</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border/20">
              <th className="px-3 py-1.5 text-left text-[9px] uppercase tracking-wider text-text-secondary font-medium">
                Player
              </th>
              <th className="px-1.5 py-1.5 text-center text-[9px] uppercase tracking-wider text-text-secondary font-medium w-8">
                Pos
              </th>
              {columns.map(col => (
                <th key={col.key} className="px-1.5 py-1.5 text-center text-[9px] uppercase tracking-wider text-text-secondary font-medium w-12">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parsedPlayers.map((player, idx) => {
              const topStreak = player.streaks.find(s => s.hit_rate >= 0.7);

              return (
                <tr
                  key={player.id || idx}
                  onClick={() => onPlayerClick?.(player)}
                  className="border-b border-border/10 hover:bg-bg-card-hover/50 transition-colors cursor-pointer"
                >
                  {/* Player name + badge + streak */}
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      {player.badge && (
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${badgeColors[player.badge] || ''}`} />
                      )}
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-text-primary truncate block">
                          {player.name}
                        </span>
                        {topStreak && (
                          <span className={`text-[9px] ${topStreak.hit_rate >= 0.8 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {topStreak.hit_rate_pct} {topStreak.description || topStreak.threshold_description || topStreak.stat_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Position */}
                  <td className="px-1.5 py-1.5 text-center">
                    <span className="text-[10px] text-text-secondary">{player.position}</span>
                  </td>

                  {/* Stat columns: season avg on top, L3 trend below */}
                  {columns.map(col => {
                    const seasonVal = player.seasonStats?.[col.key]?.value;
                    const l3Val = player.l3Stats?.[col.key]?.value;
                    const l3Color = player.l3Stats?.[col.key]?.color;

                    return (
                      <td key={col.key} className="px-1.5 py-1.5 text-center">
                        <div className="font-mono text-xs text-text-primary leading-tight">
                          {seasonVal != null && seasonVal !== '-' ? seasonVal : '-'}
                        </div>
                        {l3Val != null && l3Val !== '-' && (
                          <div className={`font-mono text-[9px] leading-tight ${getTrendColor(l3Color)}`}>
                            {l3Val}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default PlayerStatsTable;
